import { ChakraStylesConfig, GroupBase } from 'chakra-react-select';

import { isNewOption } from './new-option';
import { entryFrameStyles } from './select-styles';

export type ComboboxVariant = 'inline' | 'popover' | 'compact' | 'outline';
type MenuPlacement = 'top' | 'bottom';

function getResolvedMenuPlacement(selectProps: unknown): MenuPlacement {
  const menuProps = selectProps as {
    menuPlacement?: string;
    resolvedMenuPlacement?: MenuPlacement;
  };

  if (menuProps.resolvedMenuPlacement) {
    return menuProps.resolvedMenuPlacement;
  }

  return menuProps.menuPlacement === 'top' ? 'top' : 'bottom';
}

/**
 * Base `Combobox` styles, shared by all variants.
 * Before adding new css here, please check that it does not break any variant.
 */
const baseStyles = {
  container: (base, state) => ({
    ...base,
    width: 'full',
  }),
  control: ({ padding, ...base }, state) => ({
    ...base,
    ...(state.selectProps.menuIsOpen
      ? {
          bgColor: 'gray.25',
          borderColor: 'chakra-border-color',
          ...(getResolvedMenuPlacement(state.selectProps) === 'top'
            ? { borderTopRadius: 0 }
            : { borderBottomRadius: 0 }),
          _dark: {
            bgColor: 'gray.700',
          },
        }
      : {
          borderColor: 'transparent',
          _focusWithin: state.selectProps.isReadOnly
            ? {}
            : {
                bgColor: 'gray.25',
                borderColor: 'chakra-border-color',
                _dark: {
                  bgColor: 'gray.800',
                },
              },
          _hover: state.selectProps.isReadOnly
            ? {}
            : {
                cursor: 'pointer',
                bgColor: 'gray.100',
                _dark: {
                  bgColor: 'gray.600',
                },
              },
        }),
  }),
  valueContainer: (base) => ({
    ...base,
    alignItems: 'center',
    gap: 2,
    py: 1.5,
  }),
  // Grow the empty creatable input beyond min-content so remaining control space stays clickable.
  // Keep the previous ~24px input row height and center selected Tag `sm` chips within it.
  inputContainer: (base) => ({
    ...base,
    flex: '1 1 auto',
    gridTemplateColumns: '0 minmax(min-content, 100%)',
    alignItems: 'center',
    fontSize: 'sm',
    lineHeight: 6,
  }),
  input: (base) => ({
    ...base,
    fontSize: 'sm',
    lineHeight: 6,
  }),
  multiValue: (base) => ({
    ...base,
    m: 0,
    alignItems: 'center',
  }),
  menu: (base, state) => {
    const placement = getResolvedMenuPlacement(state.selectProps);

    return {
      ...base,
      m: 0,
      ...(placement === 'top'
        ? {
            borderBottom: 'none',
            borderBottomRadius: 0,
          }
        : {
            borderTop: 'none',
            borderTopRadius: 0,
          }),
    };
  },
  menuList: (base, state) => {
    const placement = getResolvedMenuPlacement(state.selectProps);
    return {
      ...base,
      p: 0,
      ...(placement === 'top'
        ? {
            borderBottom: 'none',
            borderBottomRadius: 0,
          }
        : {
            borderTop: 'none',
            borderTopRadius: 0,
          }),
    };
  },
  option: (base, state) => ({
    ...base,
    ...(isNewOption(state.data)
      ? {
          minH: 10,
          mt: 2,
          borderTopWidth: '1px',
          borderColor: 'chakra-border-color',
          ...(!state.isFocused && {
            bgColor: 'gray.25',
            _dark: {
              bgColor: 'gray.700',
            },
          }),
        }
      : {}),
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: 'xs',
    m: 0,
    _dark: {
      color: 'gray.500',
    },
  }),
} satisfies ChakraStylesConfig<object>;

/**
 * Variant-specific `Combobox` styles, which extend base styles above
 */
const variantStyles: Record<ComboboxVariant, ChakraStylesConfig<object>> = {
  inline: {
    ...baseStyles,
    control: (base, state) => ({
      ...baseStyles.control(base, state),
      ...(state.selectProps.menuIsOpen && {
        borderWidth: '1px',
      }),
      _disabled: {
        opacity: 1,
      },
      minH: 'auto',
      borderWidth: '1px',
    }),
    inputContainer: (base) => ({
      ...baseStyles.inputContainer(base),
      p: 0,
      m: 0,
    }),
    input: (base) => ({
      ...baseStyles.input(base),
    }),
    valueContainer: (base) => ({
      ...baseStyles.valueContainer(base),
      px: 2,
    }),
  },

  popover: {
    ...baseStyles,
    control: (base, state) => ({
      ...baseStyles.control(base, state),
      ...(state.selectProps.menuIsOpen && {
        borderBottomWidth: '1px',
        px: 4,
      }),
    }),
    menu: (base, state) => ({
      ...baseStyles.menu(base, state),
      position: 'relative',
    }),
    menuList: (base, state) => ({
      ...baseStyles.menuList(base, state),
      border: 'none',
      shadow: 'none',
    }),
    valueContainer: (base) => ({
      ...baseStyles.valueContainer(base),
      minH: '52px',
    }),
  },

  compact: {
    ...baseStyles,
    control: (base, state) => ({
      ...baseStyles.control(base, state),
      minH: 12,
      ...(state.selectProps.menuIsOpen && {
        borderBottomWidth: '1px',
        px: 3,
      }),
    }),
    inputContainer: (base) => ({
      ...baseStyles.inputContainer(base),
      p: 0,
      m: 0,
    }),
    input: (base) => ({
      ...baseStyles.input(base),
    }),
    menu: (base, state) => ({
      ...baseStyles.menu(base, state),
      position: 'relative',
    }),
    menuList: (base, state) => ({
      ...baseStyles.menuList(base, state),
      border: 'none',
      shadow: 'none',
    }),
    placeholder: (base) => ({
      ...baseStyles.placeholder(base),
      fontSize: 'sm',
      lineHeight: 5,
      ml: 0.5,
    }),
    valueContainer: (base) => ({
      ...baseStyles.valueContainer(base),
      minH: 12,
      px: 0,
      py: 2.5,
    }),
  },

  outline: {
    ...baseStyles,
    control: (base, state) => ({
      ...baseStyles.control(base, state),
      ...entryFrameStyles,
      // The ghost variant's focus fill would recolor a framed field; the frame shows focus instead.
      _focusWithin: {},
      minH: 'auto',
      borderWidth: '1px',
      ...(state.selectProps.isReadOnly
        ? {
            opacity: 0.5,
          }
        : {}),
      _hover: state.selectProps.isReadOnly
        ? {}
        : {
            cursor: 'pointer',
            borderColor: 'var(--ck-border-active)',
          },
    }),
    inputContainer: (base) => ({
      ...baseStyles.inputContainer(base),
      p: 0,
      m: 0,
    }),
    input: (base) => ({
      ...baseStyles.input(base),
    }),
    valueContainer: (base) => ({
      ...baseStyles.valueContainer(base),
      px: 3,
      py: '3px',
    }),
    placeholder: (base) => ({
      ...baseStyles.placeholder(base),
      fontSize: 'sm',
      color: 'gray.500',
      m: 0,
      _dark: {
        color: 'gray.500',
      },
    }),
  },
};

export function getChakraStyles<
  TOption extends object,
  IsMulti extends boolean,
  TGroup extends GroupBase<TOption>,
>(variant: ComboboxVariant) {
  // we are not using option specific properties during styling - therefore can safely cast here,
  // instead of wrapping styles in another function and propagating the generics
  return variantStyles[variant] as unknown as ChakraStylesConfig<TOption, IsMulti, TGroup>;
}
